// Hosted solution

import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PuffLoader from "react-spinners/PuffLoader";
import "./gateway.scss";
import "react-toastify/dist/ReactToastify.css";
import { ReactComponent as UserSvg } from "../../assets/icons/user.svg";
import { ReactComponent as CardSvg } from "../../assets/icons/card.svg";
import { ReactComponent as CalendarSvg } from "../../assets/icons/calendar.svg";

const publicUrlSandbox = "https://sandbox.touragate.com";
const publicUrlProduction = "https://api.touragate.com";
const publicUrlDev = "https://dev-touragate-d55e1.aws.spicaengine.com/api";

const endpoint = {
  prod: `${publicUrlProduction}/fn-execute/payment/credit-card`,
  dev: `${publicUrlDev}/fn-execute/payment/credit-card`,
  sandbox: `${publicUrlSandbox}/fn-execute/payment/credit-card`,
};

const month = ["Ay", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const year = [
  "Yıl",
  "2026",
  "2027",
  "2028",
  "2029",
  "2030",
  "2031",
  "2032",
  "2033",
  "2034",
  "2035",
];

const Gateway = () => {
  const { txnId, env } = useParams();

  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const sessionId = params.get("sessionId");

  const [loading, setLoading] = useState(true);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [showError, setShowError] = useState(false);
  const [provData, setProvData] = useState();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm();

  useEffect(() => {
    getTxnData();
  }, []);

  const getTxnData = async () => {
    try {
      const res = await fetch(`${endpoint[env || "sandbox"]}/${txnId}/${sessionId}`);
      const resJson = await res.json();

      if (!resJson.status) {
        toast.error(resJson.message);
        setLoading(false);
        return;
      }

      setProvData(resJson);
      setLoading(false);
    } catch (error) {
      toast.error("Hata! Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data) => {
    if (!data) return;

    setIsSubmiting(true);

    const metaData = {
      browserUserAgent: navigator.userAgent,
      language: navigator.language,
      colorDepth: window.screen.colorDepth,
      screenHeight: window.screen.height,
      screenWidth: window.screen.width,
      browserTZ: new Date().getTimezoneOffset(),
      javaEnabled: navigator.javaEnabled(),
      acceptHeader: "/",
    };

    const creditCard = { ...data.creditCard, number: data.creditCard.number.replace(/\s/g, "") };

    const dataObj = {
      creditCard,
      browserInfo: metaData,
    };

    startPayment(dataObj);
  };


  const startPayment = async (data) => {
    try {
      if (!isChValid(data.creditCard)) {
        toast.error("Geçersiz kart bilgisi");
        setIsSubmiting(false);
        return;
      }

      const response = await fetch(`${endpoint[env || "sandbox"]}/${txnId}/${sessionId}/pay`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();
      if (result?.hasOwnProperty("redirectUrl")) {
        window.location.href = result.redirectUrl;
        return;
      }
      setShowError(true);
    } catch (error) {
      setShowError(true);
      setIsSubmiting(false);
    }
  };

  const isChValid = (data) => {
    const regexHolder = /^([a-zA-Z]+[ '-]?)*[a-zA-Z]+$/;
    const regexCvv = /^\d{3,4}$/;

    if (
      !regexHolder.test(data.holder) ||
      !regexCvv.test(data.cvv) ||
      !isCreditCardNumberValid(data.number)
    ) {
      return false;
    }

    return true;
  };

  const isCreditCardNumberValid = (cardNumber) => {
    const cardDigits = cardNumber.replace(/\s/g, "").split("").map(Number);
    cardDigits.reverse();

    let sum = 0;
    let isEven = false;

    for (let i = 0; i < cardDigits.length; i++) {
      let digit = cardDigits[i];

      if (isEven) {
        digit = digit * 2 - (digit > 4 ? 9 : 0);
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  };

  const handleCardNumberChange = (event) => {
    const rawValue = event.target.value.replace(/\s/g, "");
    const formattedValue = rawValue
      .replace(/\D/g, "")
      .replace(/(.{4})/g, "$1 ")
      .trim();

    setValue("creditCard.number", formattedValue);
  };

  return (
    <section className="payment-section">
      {loading ? (
        <PuffLoader color="#36d7b7" />
      ) : showError ? (
        <section className="result-section">
          <div className="result-container">
            <div className="result-content">
              <div className="success-message">
                <div className="failure">
                  <div className="failure-icon">
                    <i>×</i>
                  </div>
                  <h1>Ödeme Başarısız</h1>
                  <p>Lütfen daha sonra tekrar deneyin</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        provData && (
          <div className="payment-form">
            <div className="form-container">
              <div className="form-content">
                <h3>Ödeme Bilgileri</h3>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="form-group">
                    <label>
                      <CardSvg /> Kart Numarası
                    </label>
                    <input
                      type="tel"
                      minLength="16"
                      maxLength="23"
                      value={watch("creditCard.number")}
                      {...register("creditCard.number", {
                        required: true,
                        minLength: 16,
                        maxLength: 23,
                        onChange: handleCardNumberChange,
                      })}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <UserSvg /> Kart Üzerindeki İsim
                    </label>
                    <input
                      type="text"
                      maxLength="100"
                      {...register("creditCard.holder", {
                        required: true,
                      })}
                    />
                  </div>
                  <div className="row">
                    <div className="form-group">
                      <label>
                        <CalendarSvg /> Son Kullanma Tarihi
                      </label>
                      <div className="row">
                        <select
                          {...register("creditCard.expiryMonth", {
                            required: true,
                            validate: (value) => value !== "Ay",
                          })}
                        >
                          {month.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>

                        <select
                          {...register("creditCard.expiryYear", {
                            required: true,
                            validate: (value) => value !== "Yıl",
                          })}
                        >
                          {year.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>
                        <CardSvg /> CVV
                      </label>
                      <div className="cvv">
                        <input
                          type="tel"
                          className="card-input"
                          maxLength="4"
                          minLength="3"
                          {...register("creditCard.cvv", {
                            required: true,
                            maxLength: 4,
                            minLength: 3,
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="pay-button"
                    disabled={Object.keys(errors).length > 0 || isSubmiting}
                  >
                    {isSubmiting ? (
                      <PuffLoader color="#fff" size={20} />
                    ) : (
                      `Öde ${provData?.amount} ${provData?.currency}`
                    )}
                  </button>
                </form>
              </div>
            </div>
            <div></div>
          </div>
        )
      )}
    </section>
  );
};

export default Gateway;
